import { AlertBlock } from "genesys-react-components";
import React, { useEffect, useState } from "react";
import "../App.css";
import { downloadPromptMediaResource, getArchitectPrompts } from "../utilities/genesysCloudUtils";
import { Loading } from "./Loading"
import { CatalogTableSurface } from "./CatalogTableSurface";

export function Prompts({
  setPageCount,
  pageNumber,
  setPageNumber,
  setOpts,
  opts,
  filters,
  setTabName,
  setIsVisible,
  selectedExportRows = {},
  exportStatus,
  onClearExportSelection,
  onExportCsv,
  onToggleExportRow,
  onToggleVisibleExportRows,
}) {
  const [prompts, setPrompts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadState, setDownloadState] = useState({ mediaUri: "", error: "" });
  const [batchDownloadState, setBatchDownloadState] = useState({
    active: false,
    completed: 0,
    total: 0,
  });
  console.log("Prompts OPTS", opts);

  const sanitizeFilenamePart = (value) => {
    return String(value || "")
      .normalize("NFC")
      .replace(/[\\/:*?"<>|]+/g, "-")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const getSuggestedFilename = (prompt, resource) => {
    const promptName = sanitizeFilenamePart(prompt?.name || "prompt");
    const language = sanitizeFilenamePart(resource?.language || "resource");
    return `${promptName}-${language}.wav`;
  };

  const handleDownloadResource = async (prompt, resource) => {
    setDownloadState({ mediaUri: resource.mediaUri, error: "" });

    try {
      await downloadPromptMediaResource(resource.mediaUri, getSuggestedFilename(prompt, resource));
      setDownloadState({ mediaUri: "", error: "" });
    } catch (downloadError) {
      console.error("Error downloading prompt media resource:", downloadError);
      setDownloadState({
        mediaUri: "",
        error: downloadError?.message || "Failed to download prompt media resource.",
      });
    }
  };

  const handleBatchDownloadSelectedResources = async () => {
    const selectedPrompts = Object.values(selectedExportRows || {});

    if (selectedPrompts.length === 0) {
      setDownloadState({ mediaUri: "", error: "Select one or more prompts first." });
      return;
    }

    const seenUris = new Set();
    const selectedResources = selectedPrompts.flatMap((prompt) =>
      (prompt.resources || [])
        .filter((resource) => resource.mediaUri)
        .filter((resource) => {
          if (seenUris.has(resource.mediaUri)) {
            return false;
          }
          seenUris.add(resource.mediaUri);
          return true;
        })
        .map((resource) => ({ prompt, resource }))
    );

    if (selectedResources.length === 0) {
      setDownloadState({ mediaUri: "", error: "Selected prompts do not have any downloadable media URIs." });
      return;
    }

    setDownloadState({ mediaUri: "", error: "" });
    setBatchDownloadState({
      active: true,
      completed: 0,
      total: selectedResources.length,
    });

    const failedDownloads = [];

    for (let index = 0; index < selectedResources.length; index += 1) {
      const { prompt, resource } = selectedResources[index];

      try {
        await downloadPromptMediaResource(resource.mediaUri, getSuggestedFilename(prompt, resource));
      } catch (downloadError) {
        failedDownloads.push({ promptName: prompt.name, language: resource.language });
      } finally {
        setBatchDownloadState((currentState) => ({
          ...currentState,
          completed: index + 1,
        }));
      }
    }

    setBatchDownloadState((currentState) => ({
      ...currentState,
      active: false,
    }));

    if (failedDownloads.length > 0) {
      setDownloadState({
        mediaUri: "",
        error: `Downloaded ${selectedResources.length - failedDownloads.length}/${selectedResources.length} files. Some media files could not be downloaded.`,
      });
    }
  };

  useEffect(() => {
    generatePrompts();
  }, [pageNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTabName("prompts-tab");
    setOpts({ name: "" })
    setIsVisible({ divisions: false, object: true, applyButton: true })
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPageNumber(1);
    generatePrompts();
    console.log("Generating Prompts with filters:", filters);
  }, [filters.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const generatePrompts = (resetPage = false) => {
    const currentPage = resetPage ? 1 : pageNumber;

    if (resetPage && pageNumber !== 1) {
      setPageNumber(1);
    }

    let opts = {
      "pageNumber": currentPage, // Number | Page number
      "pageSize": 500, // Number | Page size
      "name": [], // [String] | Name
      // "description": "description_example", // String | Description
      // "nameOrDescription": "", // String | Name or description
      "sortBy": "name", // String | Sort by
      "sortOrder": "asc", // String | Sort order
      "includeMediaUris": true, // Boolean | Include the media URIs for each resource
      "includeResources": true, // Boolean | Include the resources for each system prompt
      // "language": ["language_example"] // [String] | Filter the resources down to the provided languages
    };

    if (filters.name && filters.name.length > 0) {
      opts.name.push("*" + filters.name + "*");
    }

    getArchitectPrompts(opts)
      .then((data) => {
        setPrompts(data.entities);
        setPageCount(data.pageCount);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching prompts:", error);
        setError("Error fetching prompts: " + error);
        setLoading(false);
      });
  };

  const allVisibleSelected = prompts.length > 0 && prompts.every((prompt) => selectedExportRows[prompt.id || prompt.name]);
  const selectedPromptCount = Object.keys(selectedExportRows).length;
  const batchDownloadLabel = batchDownloadState.active
    ? `Downloading ${batchDownloadState.completed}/${batchDownloadState.total}`
    : `Download media (${selectedPromptCount})`;

  return (
    <CatalogTableSurface
      allVisibleSelected={allVisibleSelected}
      customActions={[
        {
          id: "download-selected-media",
          label: batchDownloadLabel,
          onClick: handleBatchDownloadSelectedResources,
          disabled: selectedPromptCount === 0 || batchDownloadState.active,
          title: selectedPromptCount === 0
            ? "Select one or more prompts to batch download media files."
            : "Download all media resources from selected prompts.",
        },
      ]}
      exportStatus={exportStatus}
      filteredExportAvailable={true}
      loading={loading}
      onClearSelection={onClearExportSelection}
      onExportCurrent={() => onExportCsv({ mode: "current", records: prompts })}
      onExportFiltered={() => onExportCsv({ mode: "all" })}
      onExportSelected={() => onExportCsv({ mode: "selected" })}
      onSelectVisible={(checked) => onToggleVisibleExportRows(prompts, checked)}
      rowCount={prompts.length}
      selectedCount={selectedPromptCount}
      title="Prompts"
    >
      {loading && <Loading text="Loading Prompts..." />}
      {error && <AlertBlock
        title={error}
        alertType="critical"
        className="clazzy-alertblock error-search"
      />}
      {downloadState.error && <AlertBlock
        title={downloadState.error}
        alertType="critical"
        className="clazzy-alertblock error-search"
      />}
      {!error && !loading && prompts && (
        <table className="table">
          <thead>
            <tr>
              <th aria-label="Select rows" className="select-column"></th>
              <th className="count-column">Count</th>
              <th>Prompt Name</th>
              <th>Description</th>
              <th>Resources</th>
              <th>Prompt Id</th>
            </tr>
          </thead>
          {prompts ? (
            <tbody>
              {prompts.map((prompt, index) => {
                return (
                  <tr key={index + 1}>
                    <td className="select-column">
                      <input
                        aria-label={`Select ${prompt.name} for export`}
                        checked={Boolean(selectedExportRows[prompt.id || prompt.name])}
                        onChange={(event) => onToggleExportRow(prompt, event.target.checked)}
                        type="checkbox"
                      />
                    </td>
                    <th className="index count-column">
                      {" "}
                      {pageNumber === 1
                        ? index + 1
                        : (pageNumber - 1) * 100 + index + 1}
                    </th>
                    <td>{prompt.name}</td>
                    <td className="prompt-description">{prompt.description}</td>

                    <td>
                      {prompt.resources.map((resource, index) => (
                        <div className="collapse collapse-arrow" key={index + 1}>
                          <input type="checkbox" />
                          <div className="collapse-title">{resource.language}</div>
                          <div className="collapse-content">
                            <table className="table-xs">
                              <thead>
                                <tr>
                                  <th>Language:</th>
                                  <td>{resource.language}</td>
                                </tr>
                                {resource.ttsString && (
                                  <tr>
                                    <th>TTS:</th>
                                    <td>
                                      <div className="prompt-tts-wrap">{resource.ttsString}</div>
                                    </td>
                                  </tr>
                                )}
                                {resource.text && (
                                  <tr>
                                    <th>Text:</th>
                                    <td><div className="prompt-tts-wrap">{resource.text}</div></td>
                                  </tr>
                                )}
                                {resource.durationSeconds && (
                                  <tr>
                                    <th>Duration (s):</th>
                                    <td>{resource.durationSeconds}</td>
                                  </tr>                             )}
                                {resource.mediaUri && (
                                  <tr>
                                    <th>Media URI:</th>
                                    <td>
                                      <a href={resource.mediaUri} target="_blank" rel="noreferrer">Open</a> |
                                      {" "}
                                      <a
                                        aria-disabled={downloadState.mediaUri === resource.mediaUri}
                                        href={resource.mediaUri}
                                        onClick={(event) => {
                                          event.preventDefault();
                                          if (downloadState.mediaUri !== resource.mediaUri) {
                                            handleDownloadResource(prompt, resource);
                                          }
                                        }}
                                        rel="noreferrer"
                                      >
                                        {downloadState.mediaUri === resource.mediaUri ? "Downloading..." : "Download"}
                                      </a>
                                    </td>
                                    {/* <td><kbd className="kbd">{resource.mediaUri}</kbd></td> */}
                                  </tr>
                                )}

                              </thead>
                              <tbody></tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </td>

                    <td><kbd className="kbd">{prompt.id}</kbd></td>
                  </tr>
                );
              })}
            </tbody>
          ) : (
            <h1>Loading...</h1>
          )}
        </table>
      )}
    </CatalogTableSurface>
  );
}
